import { MapPin, BarChart3, Link2, Shuffle } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { useHealth, useHealthMetrics, useHealthInsights } from '@/hooks/useHealth'

interface HealthScoreProps {
  healthScore: number
  detailed?: boolean
  showActions?: boolean
}

// Normalize values: if > 1, assume 0-100 scale and convert to 0-1
const normalize = (v: number) => v > 1 ? v / 100 : v

export default function HealthScore({
  healthScore,
  detailed = true
}: HealthScoreProps) {
  const { data: healthData } = useHealth()
  const { data: metricsData } = useHealthMetrics()
  // Steve Jobs: Removed recommendations display, keeping hook for future use
  void useHealthInsights()

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'text-emerald-400'
    if (score >= 0.6) return 'text-amber-400'
    if (score >= 0.4) return 'text-orange-400'
    return 'text-red-400'
  }

  // Steve Jobs simplification: 3 states instead of 6
  const getScoreLabel = (score: number) => {
    if (score >= 0.7) return 'HEALTHY'
    if (score >= 0.4) return 'DEVELOPING'
    return 'ATTENTION'
  }

  const getProgressColor = (score: number) => {
    if (score >= 0.8) return 'bg-emerald-400'
    if (score >= 0.6) return 'bg-amber-400'
    if (score >= 0.4) return 'bg-orange-400'
    return 'bg-red-400'
  }

  const healthMetrics = [
    {
      key: 'domain_coverage',
      label: 'Coverage',
      icon: <MapPin className="w-4 h-4" />,
      value: normalize(metricsData?.domain_coverage ?? healthData?.domain_coverage ?? 0)
    },
    {
      key: 'avg_depth',
      label: 'Depth',
      icon: <BarChart3 className="w-4 h-4" />,
      value: normalize(metricsData?.avg_depth ?? healthData?.avg_depth ?? 0)
    },
    {
      key: 'connectivity',
      label: 'Density',
      icon: <Link2 className="w-4 h-4" />,
      value: normalize(metricsData?.connectivity ?? healthData?.connectivity ?? 0)
    },
    {
      key: 'contradictions_resolved',
      label: 'Resolution',
      icon: <Shuffle className="w-4 h-4" />,
      value: normalize(metricsData?.contradictions_resolved ?? healthData?.contradictions_resolved ?? 0)
    }
  ]

  return (
    <div className="bg-transparent space-y-6">
      <div className="flex items-center justify-between px-4 pt-4">
        <h3 className="text-xs font-bold text-rsb-muted uppercase tracking-widest flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          System Integrity
        </h3>
        <span className="text-[10px] font-mono text-rsb-muted">ID: SYS-CORE-01</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 px-6">
        {/* Main Score Gauge — custom SVG kept as-is */}
        <div className="flex flex-col items-center justify-center relative">
          <div className="relative w-40 h-40">
            {/* Background Circle */}
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="45" stroke="#1A1A20" strokeWidth="4" fill="none" />
              <circle
                cx="50" cy="50" r="45"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
                strokeDasharray={`${healthScore * 283} 283`}
                className={`${getScoreColor(healthScore)} transition-all duration-1000 ease-out`}
                strokeLinecap="butt"
              />
            </svg>

            {/* Inner Content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-4xl font-display font-bold text-white tracking-tighter">
                {Math.round(healthScore * 100)}
                <span className="text-sm align-top ml-1 text-rsb-muted">%</span>
              </div>
              <div className={`text-[10px] font-bold tracking-widest uppercase mt-1 ${getScoreColor(healthScore)}`}>
                {getScoreLabel(healthScore)}
              </div>
            </div>

            {/* Steve Jobs: Removed decorative elements - the circle is sufficient */}
          </div>
        </div>

        {/* Detailed Metrics List */}
        {detailed && (
          <div className="grid grid-cols-1 gap-3 content-center">
            {healthMetrics.map((metric) => (
              <div key={metric.key} className="flex items-center justify-between group">
                <div className="flex items-center gap-3">
                  <div className={`p-1.5 rounded bg-white/5 text-rsb-muted group-hover:text-white transition-colors`}>
                    {metric.icon}
                  </div>
                  <span className="text-xs font-medium text-rsb-text-secondary uppercase tracking-wide">{metric.label}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Progress
                    value={metric.value * 100}
                    className="w-24 h-1.5 bg-white/5"
                    indicatorClassName={getProgressColor(metric.value)}
                  />
                  <span className="text-xs font-mono font-bold text-white w-8 text-right">
                    {Math.round(metric.value * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Steve Jobs: Single top action only - focus on one thing */}
      {healthData?.top_action && (
        <div className="px-6 pb-6">
          <div className="p-3 bg-rsb-accent/10 border border-rsb-accent/20 rounded-lg">
            <p className="text-sm text-white leading-tight">{healthData.top_action}</p>
          </div>
        </div>
      )}
    </div>
  )
}
